import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { petGuardianshipAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';
import { getUserPlaceholderImage } from '../../utils/imageUtils';
import { getUserTypeColor } from '../../utils/userTypeUtils';
import InviteGuardianModal from '../modals/InviteGuardianModal';

const GuardianshipList = ({ petId, isOwner }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [guardians, setGuardians] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [processing, setProcessing] = useState(null);
    const userRoleColor = getUserTypeColor(user?.user_type);

    const fetchGuardians = async () => {
        try {
            setLoading(true);
            const response = await petGuardianshipAPI.getPetGuardians(petId);
            setGuardians(response.data.data || response.data || []);
        } catch (error) {
            console.error('Error fetching guardians:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (petId) {
            fetchGuardians();
        }
    }, [petId]);

    const handleRemoveGuardian = async (guardianId) => {
        if (!window.confirm(t('petProfile.guardians.confirmRemove'))) return;

        setProcessing(guardianId);
        try {
            await petGuardianshipAPI.removeGuardian(petId, guardianId);
            showSuccessToast(t('common.success'));
            fetchGuardians();
        } catch (error) {
            console.error('Error removing guardian:', error);
            showErrorToast(t('common.error'));
        } finally {
            setProcessing(null);
        }
    };

    const handleLeaveGuardianship = async () => {
        if (!window.confirm(t('petProfile.guardians.confirmLeave'))) return;

        setProcessing('leave');
        try {
            await petGuardianshipAPI.leaveGuardianship(petId);
            showSuccessToast(t('common.success'));
            // Redirect or refresh? Probably redirect to pets list since they lost access
            window.location.href = '/pets';
        } catch (error) {
            console.error('Error leaving guardianship:', error);
            showErrorToast(t('common.error'));
            setProcessing(null);
        }
    };

    if (loading) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>{t('common.loading')}</div>;
    }

    return (
        <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', border: '1px solid #E5E7EB' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 4px 0' }}>
                        {t('petProfile.guardians.title')}
                    </h3>
                    <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                        {t('petProfile.guardians.description')}
                    </p>
                </div>
                {isOwner && (
                    <button
                        onClick={() => setShowInviteModal(true)}
                        style={{
                            background: userRoleColor,
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                        {t('petProfile.guardians.inviteButton')}
                    </button>
                )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {guardians.map((guardian) => {
                    // Assuming pivot data structure: guardian.pivot.role, guardian.pivot.status
                    // Or direct fields if API returns flattened structure
                    const role = guardian.pivot?.role || guardian.role || 'guardian';
                    const status = guardian.pivot?.status || guardian.status || 'accepted';
                    const isPrimary = role === 'primary';
                    const isMe = user?.id === guardian.id;

                    return (
                        <div key={guardian.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px',
                            background: '#F9FAFB',
                            borderRadius: '8px',
                            border: '1px solid #F3F4F6'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <img
                                    src={guardian.profile_image_url || getUserPlaceholderImage(guardian)}
                                    alt={guardian.name}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/icons/person.png';
                                    }}
                                    style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }}
                                />
                                <div>
                                    <div style={{ fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {guardian.first_name ? `${guardian.first_name} ${guardian.last_name || ''}` : guardian.name}
                                        {isPrimary && (
                                            <span style={{
                                                fontSize: '11px',
                                                background: '#FEF3C7',
                                                color: '#D97706',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                textTransform: 'uppercase',
                                                fontWeight: 700
                                            }}>
                                                {t('petProfile.guardians.primaryOwner')}
                                            </span>
                                        )}
                                        {status === 'pending' && (
                                            <span style={{
                                                fontSize: '11px',
                                                background: '#E0F2FE',
                                                color: '#0284C7',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                textTransform: 'uppercase',
                                                fontWeight: 700
                                            }}>
                                                {t('petProfile.guardians.status.pending')}
                                            </span>
                                        )}
                                    </div>

                                </div>
                            </div>

                            <div>
                                {isOwner && !isPrimary && !isMe && (
                                    <button
                                        onClick={() => handleRemoveGuardian(guardian.id)}
                                        disabled={processing === guardian.id}
                                        style={{
                                            color: '#EF4444',
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            cursor: processing === guardian.id ? 'default' : 'pointer',
                                            opacity: processing === guardian.id ? 0.5 : 1
                                        }}
                                    >
                                        {processing === guardian.id ? '...' : t('petProfile.guardians.removeGuardian')}
                                    </button>
                                )}
                                {!isPrimary && isMe && (
                                    <button
                                        onClick={handleLeaveGuardianship}
                                        disabled={processing === 'leave'}
                                        style={{
                                            color: '#EF4444',
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            cursor: processing === 'leave' ? 'default' : 'pointer',
                                            opacity: processing === 'leave' ? 0.5 : 1
                                        }}
                                    >
                                        {processing === 'leave' ? '...' : t('petProfile.guardians.leaveGuardianship')}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <InviteGuardianModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                petId={petId}
                currentGuardians={guardians}
                onInviteSent={() => {
                    setShowInviteModal(false);
                    fetchGuardians();
                }}
                primaryColor={userRoleColor}
            />
        </div>
    );
};

export default GuardianshipList;
